import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import { getDevolucoes } from '../services/api';
import { useEstabelecimentoFiltroApi } from '../context/EstabelecimentoContext';

const money = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR');
};

export default function DevolucoesPage() {
  const estabelecimentoId = useEstabelecimentoFiltroApi();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [termo, setTermo] = useState('');
  const [utilizado, setUtilizado] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const { data } = await getDevolucoes({
        indice: page,
        qtde: 20,
        termo: termo.trim() || undefined,
        utilizado: utilizado === '' ? undefined : utilizado === '1',
        estabelecimentoId: estabelecimentoId || undefined,
      });
      setItems(data?.items || data?.Items || []);
      setTotalPages(data?.totalPages || data?.TotalPages || 1);
    } catch (e) {
      setErro(e?.response?.data?.error || e.message || 'Falha ao carregar vales');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, termo, utilizado, estabelecimentoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return (
    <Box>
      <PageHeader
        title="Vales troca"
        subtitle="Devoluções de cliente geradas no PDV (crédito para troca de mercadoria)."
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Buscar cliente"
            size="small"
            value={termo}
            onChange={(e) => {
              setPage(1);
              setTermo(e.target.value);
            }}
            fullWidth
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Situação</InputLabel>
            <Select
              label="Situação"
              value={utilizado}
              onChange={(e) => {
                setPage(1);
                setUtilizado(e.target.value);
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="0">Abertos</MenuItem>
              <MenuItem value="1">Utilizados</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        {erro ? (
          <Typography color="error" sx={{ mt: 1 }}>
            {erro}
          </Typography>
        ) : null}
      </Paper>

      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nº</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell>Venda</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>Carregando…</TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>Nenhum vale encontrado.</TableCell>
              </TableRow>
            ) : (
              items.map((row) => (
                <TableRow key={row.id || row.Id}>
                  <TableCell>{row.id ?? row.Id}</TableCell>
                  <TableCell>{formatDate(row.dataCadastro || row.DataCadastro)}</TableCell>
                  <TableCell>{row.clienteNome || row.ClienteNome || '—'}</TableCell>
                  <TableCell align="right">{money(row.valor ?? row.Valor)}</TableCell>
                  <TableCell>
                    {(row.utilizado ?? row.Utilizado) ? (
                      <Chip size="small" color="default" label="Utilizado" />
                    ) : (
                      <Chip size="small" color="success" label="Aberto" />
                    )}
                  </TableCell>
                  <TableCell>{row.vendaId || row.VendaId || '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {totalPages > 1 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            page={page}
            count={totalPages}
            onChange={(_, p) => setPage(p)}
            color="primary"
          />
        </Box>
      ) : null}
    </Box>
  );
}
